/** @file Defines the PromiseButton, wraps Button with state management and issue displaying. */
import moment from 'moment';
import Vue from 'vue';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Check, Close, Failure, LoaderCircle, TriangleAlert } from '@codegrade/icons';
import { NonEmptyList } from '@codegrade/non-empty-list';
import { assert, errors, expr, promise } from '@codegrade/utils';
import { Icon } from '@/components/Icon';
import { Popover } from '@/components/Popover/Popover';
import * as utils from '@/utils';
import { baseButtonProps, Button, } from './Button';
/** The classes on a popover of a PromiseButton, conveying the state using semantic. */
const POPOVER_SEMANTIC_CLASSES = {
    highlighted: [
        `text-highlighted-60`,
        `bg-highlighted-5`,
        `dark:text-highlighted-10`,
        `dark:bg-highlighted-60`,
    ],
    critical: [`text-critical-60`, `bg-critical-5`, `dark:text-critical-10`, `dark:bg-critical-60`],
    actionable: [
        `text-actionable-60`,
        `bg-actionable-5`,
        `dark:text-actionable-10`,
        `dark:bg-actionable-60`,
    ],
    neutral: [`text-neutral-60`, `bg-neutral-5`, `dark:text-neutral-10`, `dark:bg-neutral-60`],
    positive: [`text-positive-60`, `bg-positive-5`, `dark:text-positive-10`, `dark:bg-positive-60`],
};
/** Props of the `PromiseButton` component. */
export const promiseButtonProps = {
    ...baseButtonProps,
    /** The desired action the button should perform. */
    action: p.ofFunction().required,
    /** Screen reader message to inform the action has started, is loading. */
    loadingAlertMessage: p(String).required,
    /** Screen reader message to inform the action has finished successfully. */
    successAlertMessage: p(String).required,
    /** The MDN button type of this button, default: `submit`. */
    type: p.ofType().default('submit'),
    /** The label rendered in the button, default: `Submit`. */
    label: p(String).default('Submit'),
    /** The icon on the left side of the label, overwritten by slots. */
    icon: p.ofType().optional,
    /**
     * Duration in ms to show the success state.
     *
     * If 0, success state is skipped.
     * Default: 500.
     */
    successDuration: p(Number).default(500),
    /**
     * The minimum duration the spinner should show for a button.
     */
    waitAtLeast: p.ofType().default(250),
    /**
     * Whether to show any encountered error, or warning in a Popover.
     *
     * The component will reset itself when the popover is dismissed.
     * Default: `true`.
     */
    showIssuesInPopover: p(Boolean).default(true),
};
/** Button to perform an action that is a promise, with state management and issue reporting. */
export const PromiseButton = tsx.withPropsObject(tsx.componentFactoryOf().create({
    name: 'promise-button',
    inheritAttrs: false,
    props: promiseButtonProps,
    /** @returns The data for this promise button. */
    data() {
        /**
         * Workaround to remove Vue2 automatic application of classes to the root element
         * of a component, which we cannot disable like in Vue3.
         */
        const nativeClass = [this.$vnode.data?.class, this.$vnode.data?.staticClass];
        delete this.$vnode.data?.class;
        delete this.$vnode.data?.staticClass;
        const nativeStyle = [this.$vnode.data?.style, this.$vnode.data?.staticStyle];
        delete this.$vnode.data?.style;
        delete this.$vnode.data?.staticStyle;
        return {
            uniqueId: `${utils.getUniqueId()}`,
            state: { type: 'default' },
            isOpenIssuePopover: false,
            retryState: undefined,
            nativeClass,
            nativeStyle,
        };
    },
    /** Called when the component is created. */
    created() {
        this.$watch(() => this.stateType, stateType => {
            if (!this.showIssuesInPopover) {
                return;
            }
            switch (stateType) {
                case 'default':
                case 'confirming':
                case 'loading':
                case 'success':
                    break;
                case 'warning':
                case 'error':
                    this.$nextTick(() => {
                        this.$withRef('promiseButtonRef', promiseButton => {
                            const promiseButtonEl = promiseButton instanceof HTMLElement
                                ? promiseButton
                                : promiseButton.$el;
                            this.showIssuePopover(promiseButtonEl);
                        });
                    });
                    break;
                default:
                    assert.AssertionError.assertNever(stateType);
            }
        });
    },
    computed: {
        /** @returns The unique id for this PromiseButton. */
        id() {
            return this.$attrs.id ?? `promise-button-${this.uniqueId}`;
        },
        /** @returns The data-cy attribute for this PromiseButton. */
        dataCy() {
            return this.$attrs['data-cy'] ?? this.id;
        },
        /** @returns The type of this buttons current state, used for watching. */
        stateType() {
            return this.state.type;
        },
        /** @returns The confirm to use — retry confirm overrides the prop. */
        effectiveConfirm() {
            return this.retryState?.confirm ?? this.confirm;
        },
        /** @returns The unique id for the popover describing an issue. */
        issuePopoverId() {
            return `issue-popover-${this.id}`;
        },
        /** @returns The key for this button, we can reuse a button if the state, semantic and prominence are the same. */
        key() {
            return `${this.state.type}-${this.semantic}-${this.prominence}`;
        },
        /** @returns The classes to add to this button. */
        promiseButtonClasses() {
            return [
                'promise-btn',
                `state-${this.state.type}`,
                this.buttonClasses,
                this.nativeClass,
            ];
        },
        /** @returns The button semantic based on state. */
        statefulSemantic() {
            return expr.match(this.state, 'type', {
                default: () => this.semantic,
                confirming: () => this.semantic,
                loading: () => this.semantic,
                success: () => 'positive',
                warning: () => 'highlighted',
                error: () => 'critical',
            });
        },
        /**
         * Get the disabled state for the button based on the state.
         *
         * If we are in the default state, we take the `disabled` prop
         * that is set on this component. This should disable the button
         * from ever leaving the default state.
         *
         * If we are in a confirming state, we explicitly pass `false` since,
         * the `Button` handles the confirm disable.
         *
         * @returns The disabled prop to pass to the base Button based on state.
         */
        statefulDisabled() {
            const busy = {
                type: 'external',
                style: 'soft',
                ariaDescribedby: 'use-aria-live-to-announce-states',
            };
            return expr.match(this.state, 'type', {
                confirming: () => false,
                default: () => this.disabled,
                loading: () => busy,
                success: () => busy,
                warning: () => busy,
                error: () => busy,
            });
        },
        /**
         * @returns The icon to show in the button content, based on state.
         * In the `default` state, the button content is rendered through
         * (in this order):
         *
         * - The `content` scoped slot.
         * - The `default` slot.
         * - The `label` prop.
         */
        statefulIconNode() {
            return expr.match(this.state, 'type', {
                default: () => undefined,
                confirming: () => <Icon icon={LoaderCircle} spin/>,
                loading: () => utils.renderScopedSlot(undefined, this.$scopedSlots.loadingStateContent, () => <Icon icon={LoaderCircle} spin/>),
                success: () => <Icon icon={Check}/>,
                warning: () => <Icon icon={TriangleAlert}/>,
                error: () => <Icon icon={Failure}/>,
            });
        },
        /**
         * Render the content for all but default state.
         *
         * The default state must always render to ensure the button size
         * remains consistent, so we cannot render it conditionally here.
         *
         * @returns The non-default button content based on state.
         */
        statefulContentNode() {
            if (this.state.type === 'default') {
                return utils.emptyVNode();
            }
            const absoluteCenter = 'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-center';
            return <div class={absoluteCenter}>{this.statefulIconNode}</div>;
        },
        /**
         * Render an optional alert for busy states that need explanation.
         *
         * @returns An alert message for screen readers if needed.
         */
        statefulDisabledReasonNode() {
            const alertMessage = expr.match(this.state, 'type', {
                // Default state is not a busy state, thus not disabled like this.
                default: () => '',
                confirming: () => 'confirming',
                warning: () => 'warning',
                error: () => 'error',
                loading: () => this.loadingAlertMessage,
                success: () => this.successAlertMessage,
            });
            return <output class="sr-only">{alertMessage}</output>;
        },
        /** @returns The icon to display in the button, using the `icon` prop. */
        iconNode() {
            if (this.icon == null) {
                return utils.emptyVNode();
            }
            return <Icon class="mr-1" icon={this.icon}/>;
        },
    },
    methods: {
        /** @returns The content rendered inside the button. */
        renderContentNode() {
            return [
                utils.renderScopedSlot({ statusIcon: this.statefulIconNode }, this.$scopedSlots.content, () => [
                    <div class="relative grow flex items-center justify-center gap-1">
                                <div class={{
                            invisible: this.state.type !== 'default',
                            contents: true,
                        }}>
                                    {this.$slots.default ?? [this.iconNode, this.label]}
                                </div>
                            </div>,
                    this.statefulContentNode,
                ]),
                this.statefulDisabledReasonNode,
            ];
        },
        /** @returns The actual button to render. */
        renderButtonNode() {
            return (<Button attrs={this.$attrs} id={this.id} data-cy={this.dataCy} ref="promiseButtonRef" style={this.nativeStyle} buttonClasses={this.promiseButtonClasses} action={this.performAction} nativeOnBlur={this.handleOnBlur} ariaLabel={this.ariaLabel} ariaDescribedBy={this.ariaDescribedBy} data-loading={this.state.type === 'loading'} type={this.type} semantic={this.statefulSemantic} prominence={this.prominence} disabled={this.statefulDisabled} confirm={this.effectiveConfirm} onToggleConfirm={this.handleOnToggleConfirm} scopedSlots={{
                    content: this.renderContentNode,
                    confirmModalBody: () => utils.renderScopedSlot(undefined, this.$scopedSlots.confirmModalBody, () => undefined),
                }}/>);
        },
        /**
         * Set button state to success for the duration, then to default.
         *
         * @param result The result we received from performing the action.
         */
        async moveToSuccessThenDefault(result) {
            await result?.onSuccess?.();
            if (this.successDuration <= 0) {
                try {
                    await result?.onAfterSuccess?.();
                }
                catch (caught) {
                    // eslint-disable-next-line no-console
                    console.error(caught, 'On after success errored');
                }
                this.state = { type: 'default' };
                return;
            }
            const newState = { type: 'success' };
            this.state = newState;
            setTimeout(async () => {
                try {
                    await result?.onAfterSuccess?.();
                }
                catch (caught) {
                    // eslint-disable-next-line no-console
                    console.error(caught, 'On after success errored');
                }
                if (this.state === newState) {
                    this.state = { type: 'default' };
                }
            }, this.successDuration);
        },
        /**
         * Set the button state to warning.
         *
         * Makes the function available to dismiss the warning state.
         *
         * @param warnings The warnings that we have encountered in the action.
         * @param result The result that caused this warning.
         */
        moveToWarning(warnings, result) {
            let isCalled = false;
            /**
             * Dismiss the warning state of the performed action.
             *
             * Note: We still perform the onAfterSuccess here.
             *
             * @param opts Options for closing popover.
             */
            const dismiss = async (opts) => {
                if (isCalled) {
                    return;
                }
                isCalled = true;
                // When the warning is dismissed, we resume the success flow
                // by calling onAfterSuccess and then resetting to default.
                if (this.state.type !== 'warning') {
                    return;
                }
                this.moveToSuccessThenDefault({
                    warnings: result.warnings,
                    onSuccess: () => result.onSuccess?.(),
                    onAfterSuccess: async () => {
                        await result.onAfterSuccess?.();
                        if (opts.focusButtonAfterClose) {
                            this.$focusRef('promiseButtonRef');
                        }
                    },
                });
            };
            this.state = { type: 'warning', warnings, dismiss };
            tsx.emitOn(this, 'onWarning', { warnings, dismiss });
        },
        /**
         * Wraps the user-provided `action` promise with state management.
         *
         * Only the default and confirming state are allowed to perform
         * the desired action of this button.
         *
         * The `default` state allows buttons without confirm to perform the action.
         *
         * The `confirming` state support the two-step workflow for confirmable buttons.
         * 1. The button click shows a confirmation popover and moves the state
         *    to `confirming`.
         * 2. The final "Confirm" action within that popover calls this
         *    method to run the promise.
         *
         * @param evt The mouse or keyboard event from the original button.
         * @returns The promise that resolves once the action is resolved.
         */
        async performAction(evt) {
            if ((this.state.type !== 'default' && this.state.type !== 'confirming') ||
                this.disabled !== false) {
                return;
            }
            /** @returns The action result. */
            const actionToRun = this.retryState != null ? this.retryState.action : () => this.action(evt);
            const prom = Promise.all([
                actionToRun(),
                promise.sleep(moment.duration(this.waitAtLeast)),
            ]).then(([value]) => value);
            this.state = { type: 'loading', promise: prom };
            try {
                const result = await prom;
                if (result != null && result.tag === 'retry') {
                    this.retryState = result;
                    this.state = { type: 'default' };
                    // Auto-click the button so the confirm dialog shows
                    // immediately, same as SubmitButton's retry pattern.
                    this.$nextTick(() => {
                        this.$withRef('promiseButtonRef', promiseButton => {
                            const root = promiseButton instanceof HTMLElement
                                ? promiseButton
                                : promiseButton.$el;
                            const btn = root instanceof HTMLButtonElement
                                ? root
                                : root.querySelector('button');
                            if (btn instanceof HTMLButtonElement) {
                                btn.click();
                            }
                        });
                    });
                    return;
                }
                // Clear retry state after a successful retry.
                this.retryState = undefined;
                if (result != null &&
                    result.warnings != null &&
                    NonEmptyList.isNonEmpty(result.warnings.messages)) {
                    this.moveToWarning(result.warnings.messages, result);
                    return;
                }
                await this.moveToSuccessThenDefault(result ?? undefined);
            }
            catch (err) {
                // Clear retry state on error.
                this.retryState = undefined;
                // Stop faulty actions when we have encountered an error.
                if (evt instanceof MouseEvent || evt.key === 'Enter' || evt.key === 'Space') {
                    evt.preventDefault();
                    evt.stopPropagation();
                }
                let resetCalled = false;
                /**
                 * Reset the button to the default state, discarding the error.
                 *
                 * @param opts Options for closing popover.
                 */
                const reset = (opts) => {
                    if (resetCalled) {
                        return;
                    }
                    resetCalled = true;
                    if (this.state.type === 'error') {
                        this.state = { type: 'default' };
                        if (opts.focusButtonAfterClose) {
                            this.$focusRef('promiseButtonRef');
                        }
                    }
                };
                const error = errors.coerceToError(err);
                this.state = { type: 'error', error, reset };
                tsx.emitOn(this, 'onError', { error, reset });
            }
        },
        /**
         * Show the popover with the issue resulted from performing the action.
         *
         * Focus the close button of the popover so that screen readers will correctly
         * read the alert, and have the focus be ready to click the close button.
         *
         * Note: We use this method to show the popover, since changing the state
         * of our button re-renders the button, which makes our `Popover` component
         * lose track of which target to attach to.
         *
         * @param promiseButtonEl The promise button that the popover should attach to.
         */
        showIssuePopover(promiseButtonEl) {
            this.$withRef('popoverRef', popover => {
                if (popover instanceof Vue &&
                    'show' in popover &&
                    typeof popover.show === 'function') {
                    popover.show(promiseButtonEl);
                }
            }).then(() => {
                this.$focusRef('closeIssuePopoverButtonRef');
            });
        },
        /**
         * Automatically close the issue popover if focus moves outside this component.
         *
         * @param focusEvent The native FocusEvent.
         */
        handleOnBlur(focusEvent) {
            if (!this.isOpenIssuePopover) {
                return;
            }
            /**
             * Close the issue popover if focus has left this component.
             *
             * @param close Function to close the issue popover.
             */
            const closeIssuePopoverOnBlur = (close) => {
                this.$withRef('popoverRef', popover => {
                    const popoverEl = popover instanceof HTMLElement ? popover : popover.$el;
                    const target = focusEvent.relatedTarget;
                    if (target == null ||
                        (target instanceof Node && !popoverEl.contains(target))) {
                        close({ focusButtonAfterClose: false });
                    }
                });
            };
            expr.match(this.state, 'type', {
                error: ({ reset }) => closeIssuePopoverOnBlur(reset),
                warning: ({ dismiss }) => closeIssuePopoverOnBlur(dismiss),
                default: () => { },
                confirming: () => { },
                loading: () => { },
                success: () => { },
            });
        },
        /** Update state according to whether we have opened or closed the confirm. */
        handleOnToggleConfirm(evt) {
            if (this.state.type === 'default' && evt.isOpen) {
                this.state = { type: 'confirming' };
            }
            else if (this.state.type === 'confirming' && !evt.isOpen) {
                this.state = { type: 'default' };
            }
        },
        /**
         * @param semantic The semantic color for the popover.
         * @param close The function to close the popover.
         * @returns The rendered button to close the popover.
         */
        renderClosePopoverButtonNode(semantic, close) {
            return (<Button ref="closeIssuePopoverButtonRef" semantic={semantic} prominence={1} nativeOnBlur={this.handleOnBlur} nativeOnKeydown={evt => {
                    if (evt.key === 'Escape') {
                        close({ focusButtonAfterClose: false });
                    }
                }} ariaLabel="Close alert" ariaDescribedBy={this.issuePopoverId} action={() => close({ focusButtonAfterClose: true })} scopedSlots={{
                    content: () => <Icon icon={Close}/>,
                }}/>);
        },
        /**
         * Render this button with a popover to show the encountered issue.
         *
         * @param semantic Semantic color for the popover.
         * @param popover Function to render the popover content.
         * @returns Rendered issue popover.
         */
        renderIssuePopoverNode(semantic, popover) {
            return (<Popover placement="top" key="promise-button-issue-popover" popoverId={this.issuePopoverId} class="promise-button-wrapper contents" // Ensures the popover container does not affect button layout and styling.
             ref="popoverRef" variant="dialog" onToggle={isOpen => {
                    this.isOpenIssuePopover = isOpen;
                }} popoverType="manual" popoverAttrs={{
                    class: [POPOVER_SEMANTIC_CLASSES[semantic], 'p-0'],
                }} scopedSlots={{
                    // Ensure we can focus the popover itself, so we can copy the error message.
                    popover: () => (<div tabindex={-1} onBlur={this.handleOnBlur} class="p-3">
                                    {popover()}
                                </div>),
                    button: this.renderButtonNode,
                }}/>);
        },
        /**
         * @param warningState The current warning state.
         * @returns The rendered PromiseButton with popover.
         */
        renderWarningPopoverNode(warningState) {
            return this.renderIssuePopoverNode('highlighted', () => (<div class="flex flex-row items-center gap-3">
                        <div class="flex flex-col">
                            {warningState.warnings.map(w => (<div>{w.text}</div>))}
                        </div>
                        {this.renderClosePopoverButtonNode('highlighted', warningState.dismiss)}
                    </div>));
        },
        /**
         * @param errorState The current error state.
         * @returns The rendered PromiseButton with popover.
         */
        renderErrorPopoverNode(errorState) {
            return this.renderIssuePopoverNode('critical', () => (<div class="flex flex-row items-center gap-3">
                        <div>
                            {utils.renderScopedSlot(errorState, this.$scopedSlots.error, ({ error }) => utils.getErrorMessage(error))}
                        </div>
                        {this.renderClosePopoverButtonNode('critical', errorState.reset)}
                    </div>));
        },
    },
    /** @returns The rendered promise button, with possible issue popover. */
    render() {
        return expr.match(this.state, 'type', {
            default: this.renderButtonNode,
            confirming: this.renderButtonNode,
            loading: this.renderButtonNode,
            success: this.renderButtonNode,
            warning: this.renderWarningPopoverNode,
            error: this.renderErrorPopoverNode,
        });
    },
}));
